/**
 * usePlacementAntrag - TanStack Query hooks for the Ausschreibungs-Screen Tab 5a (Antrag + Signatur +
 * Einreichung, Prozessschritte 15-18; P2 Placement Slice 13). live mode hits apiClient.placements.*Antrag;
 * mock mode resolves a fixture (Ue9: Helvetia Betriebshaftpflicht, prepared + sent for signature via
 * portal). The server owns every gate; the UI only triggers + renders. While a send/Einreichung is
 * running the query refetches every 5 s.
 *
 * [Source: apps/web/src/features/placements/usePlacementKundenfreigabe.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  APPLICATION_STATUS_LABELS,
  APPLICATION_FORM_QUELLE_LABELS,
  SIGNATURE_CHANNEL_LABELS,
  type AntragFristBody,
  type AntragResponse,
  type AntragSignaturBody,
  type AntragVerwerfenBody,
  type AntragVorbereitenBody,
} from '@brokly/shared';

function mockAntrag(id: string): AntragResponse {
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'entschieden',
    closure_status: 'offen',
    branchencode: '21000',
    branchen_label: 'Betriebshaftpflicht',
    kunde_name: 'Mueller AG',
    entscheid: {
      decision_id: 'pdc-mock-1',
      decided_at: '2026-11-14T10:00:00.000Z',
      carrier_uid: 'CHE-111.111.111',
      carrier_name: 'Helvetia',
      submission_number: 'SUB-2026-000484-HAFT',
      offer_id: 'offd-helvetia',
      version_no: 1,
      praemie_jahr: 4850,
      versicherungsbeginn: '2027-01-01',
      entscheider_name: 'Hans Mueller',
      promotion_policy_id: 'pol-mock-1',
      promotion_policy_number: 'PL-2026-000001',
    },
    antrag: {
      id: 'apd-mock-1',
      version_no: 1,
      is_aktuell: true,
      status: 'zur_unterschrift_versendet',
      status_label: APPLICATION_STATUS_LABELS.zur_unterschrift_versendet,
      status_changed_at: '2026-11-17T09:00:00.000Z',
      decision_id: 'pdc-mock-1',
      offer_id: 'offd-helvetia',
      submission_id: 'sub-mock-1',
      submission_number: 'SUB-2026-000484-HAFT',
      carrier_uid: 'CHE-111.111.111',
      carrier_name: 'Helvetia',
      carrier_logo_url: null,
      policy_id: 'pol-mock-1',
      policy_number: 'PL-2026-000001',
      begin_date: '2027-01-01',
      praemie_jahr: 4850,
      versicherungssumme: 5000000,
      selbstbehalt: 500,
      vertragsdauer_monate: 12,
      bemerkung: null,
      form_quelle: 'versicherer_formular',
      form_quelle_label: APPLICATION_FORM_QUELLE_LABELS.versicherer_formular,
      carrier_form_template_ref: 'helvetia-haftpflicht-antrag',
      form_template_name: 'Helvetia-Antragsformular Betriebshaftpflicht',
      doc: {
        filename: 'Antrag_PL-2026-000001_v1.pdf',
        mime: 'application/pdf',
        size_bytes: 20480,
        sha256: 'a'.repeat(64),
        engine: 'builtin_pdf',
        rendered_at: '2026-11-17T08:55:00.000Z',
        datei_url: `/placements/${id}/antrag/dokument`,
        hidden_reason: null,
      },
      prefill_hinweise: [],
      signatur: {
        channel: 'portal',
        channel_label: SIGNATURE_CHANNEL_LABELS.portal,
        versendet_at: '2026-11-17T09:00:00.000Z',
        versendet_by_name: 'Max Broker',
        versand_count: 1,
        frist_date: '2026-12-01',
        ueberfaellig: false,
        eskaliert_at: null,
        task_id: null,
        empfaenger: null,
        esign: null,
      },
      signiert: null,
      einreichung: null,
      carrier_reply: null,
      versand_laeuft: false,
      versand_gesperrt: false,
      nachtragen_moeglich: false,
      letzter_versandfehler: null,
      letzter_versandfehler_at: null,
      verworfen: null,
      emails: [],
      created_at: '2026-11-17T08:55:00.000Z',
      updated_at: '2026-11-17T09:00:00.000Z',
    },
    versionen: [],
    vorbereiten_erlaubt: false,
    sperrgrund: 'antrag_offen',
    aktion_sperrgrund: null,
    alternative: null,
    formular: {
      form_template_ref: 'helvetia-haftpflicht-antrag',
      template_vorhanden: true,
      template_name: 'Helvetia-Antragsformular Betriebshaftpflicht',
      quelle_vorschau: 'versicherer_formular',
    },
    begin_date_vorschlag: '2027-01-01',
    praemie_jahr_vorschlag: 4850,
    kanal_vorschlag: 'portal',
    portal_verfuegbar: true,
    kunde_email: 'info@mueller-ag.example',
    esign_verfuegbar: false,
    carrier_email: 'offerten@helvetia.ch',
    frist_vorschlag: '2026-12-01',
    aktionen: {
      bearbeiten: false,
      signatur_senden: false,
      erneut_senden: true,
      frist_aendern: true,
      status_abfragen: false,
      signiert_hochladen: true,
      einreichen: false,
      verwerfen: true,
    },
  };
}

export function usePlacementAntrag(id: string, enabled = true) {
  const live = isLiveData();
  return useQuery<AntragResponse>({
    queryKey: qk.placementAntrag(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getAntrag(id) : Promise.resolve(mockAntrag(id))),
    enabled,
    refetchInterval: (query) => (live && query.state.data?.antrag?.versand_laeuft ? 5000 : false),
    ...(live ? {} : { initialData: () => mockAntrag(id), staleTime: Infinity }),
  });
}

function useAntragInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'antrag', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
  };
}

export function useVorbereitenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (body: AntragVorbereitenBody = {}) =>
      live ? apiClient.placements.vorbereitenAntrag(id, body) : Promise.resolve(mockAntrag(id)),
    onSuccess: () => invalidate(),
  });
}

export function useAktualisierenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (body: AntragVorbereitenBody) =>
      live ? apiClient.placements.aktualisierenAntrag(id, body) : Promise.resolve(mockAntrag(id)),
    onSuccess: () => invalidate(),
  });
}

export function useSignaturAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (body: AntragSignaturBody) =>
      live ? apiClient.placements.signaturAntrag(id, body) : Promise.resolve(mockAntrag(id)),
    onSuccess: () => invalidate(),
  });
}

export function useErneutSignaturAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.erneutSignaturAntrag(id) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

export function useFristAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (body: AntragFristBody) => (live ? apiClient.placements.fristAntrag(id, body) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

/** PR #43 wave 2 (#5): resolve an interrupted Antrag send (nachtragen / freigeben). */
export function useVersandAufloesenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (aktion: 'nachtragen' | 'freigeben') => (live ? apiClient.placements.versandAufloesenAntrag(id, aktion) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

export function useAbfragenSignaturAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.abfragenSignaturAntrag(id) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

export function useSigniertHochladenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (file: File) => (live ? apiClient.placements.signiertHochladenAntrag(id, file) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

export function useEinreichenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.einreichenAntrag(id) : Promise.resolve(mockAntrag(id))),
    onSuccess: () => invalidate(),
  });
}

export function useVerwerfenAntrag(id: string) {
  const live = isLiveData();
  const invalidate = useAntragInvalidate(id);
  return useMutation({
    mutationFn: (body: AntragVerwerfenBody) =>
      live ? apiClient.placements.verwerfenAntrag(id, body) : Promise.resolve(mockAntrag(id)),
    onSuccess: () => invalidate(),
  });
}
